// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
    Box,
    Button,
    Columns,
    Flex,
    Icon,
    Heading,
    Text,
    useToast
} from '@locus-taxy/neo-ui';

import FileUtils from 'common/utils/FileUtils';
import { APIErrorScreen } from 'common/components/core/errors';
import { ApiStatusEnum } from 'common/enums/API';
import { Input } from 'common/components/core/input';
import parseCSV from 'common/utils/CSVParserUtils';

// eslint-disable-next-line max-len
import { UnusedSummaryItem } from 'platform/settings/modules/orchestration/models/internal/WaybillUnusedSummaryResponse';
import { WaybillGenerationTypeEnum } from 'platform/settings/modules/orchestration/enums/WaybillGenerationTypeEnum';

import Skeleton from 'platform/settings/modules/orchestration/components/Skeleton';
import { DataTable } from 'platform/settings/modules/orchestration/components/DataTable';
import UploadButton from 'platform/settings/modules/orchestration/components/UploadButton';

import WaybillDialog from './WaybillDialog';

import useGetWaybillUnusedSummary from './useGetWaybillUnusedSummary';
import useWaybillBulkCreate from './useWaybillBulkCreate';

type Props = {
    translationPath: string
};

const PregenSeries = ({ translationPath }: Props): React.Node => {
    const [searchKey, setSearchKey] = React.useState();
    const [
        selectedCarrier,
        setSelectedCarrier
    ] = React.useState<?UnusedSummaryItem>(null);

    const { showToast, closeToast } = useToast();
    const { t } = useTranslation('platform');

    const waybillUnusedSummaryQuery = useGetWaybillUnusedSummary(searchKey);
    const { mutateAsync } = useWaybillBulkCreate();

    const showSuccessToast = React.useCallback(
        ({
            uploadedCount,
            row
        }: {
            uploadedCount: number,
            row: UnusedSummaryItem
        }) => {
            const uploadSuccess = t(`${translationPath}.uploadSuccess`);
            const waybillNumber = t(`${translationPath}.waybillNumber`, {
                count: uploadedCount
            });

            const toast = showToast({
                title: `${uploadedCount} ${waybillNumber} ${uploadSuccess}`,
                palette: 'primary',
                icon: <Icon icon="bell" size="xLarge" color="blue.400" />,
                action: t('translations:open'),
                onAction: () => {
                    setSelectedCarrier(row);
                    closeToast(toast);
                }
            });
        },
        [closeToast, showToast, t, translationPath]
    );

    const showFailedToast = React.useCallback(
        rejectedWaybillNumbers => {
            const count = rejectedWaybillNumbers.size;
            const uploadFailed = t(`${translationPath}.uploadFailed`);
            const waybillNumber = t(`${translationPath}.waybillNumber`, {
                count
            });

            const toast = showToast({
                title: `${count} ${waybillNumber} ${uploadFailed}`,
                palette: 'danger',
                icon: <Icon icon="alert_hex" size="xLarge" color="red.400" />,
                action: t('translations:view'),
                onAction: () => {
                    closeToast(toast);
                    FileUtils.createAndDownloadCSV({
                        fileName: 'waybill-errors.csv',
                        headers: ['Waybill Number', 'Error Message'],
                        rows: rejectedWaybillNumbers
                            .toJS()
                            .map(({ waybillNumber, errorMessage }) => ({
                                ['Waybill Number']: waybillNumber,
                                ['Error Message']: errorMessage
                            }))
                    });
                }
            });
        },
        [closeToast, showToast, t, translationPath]
    );

    const handleUpload = React.useCallback(
        async ({ file, row }: { file: File, row: UnusedSummaryItem }) => {
            const { carrierId, serviceType } = row;

            const inProgressToast = showToast({
                title: t(`${translationPath}.uploadInProgress`),
                palette: 'primary',
                autoHideDuration: null
            });

            try {
                const { parsedFile } = await parseCSV({
                    file,
                    config: { delimiter: ',' }
                });

                const waybillNumbers = Array.from(
                    new Set(
                        parsedFile.fileRows.map(({ waybillId }) => waybillId)
                    )
                );

                const { rejectedWaybillNumbers } = await mutateAsync({
                    carrierId,
                    serviceType,
                    waybillNumbers
                });

                closeToast(inProgressToast);

                const rejectedCount = rejectedWaybillNumbers.size;
                const uploadedCount =
                    waybillNumbers.length - rejectedWaybillNumbers.size;

                if (uploadedCount > 0) {
                    showSuccessToast({
                        uploadedCount,
                        row
                    });
                }

                if (rejectedCount > 0) {
                    showFailedToast(rejectedWaybillNumbers);
                }
            } catch (error) {
                closeToast(inProgressToast);

                showToast({
                    title: t(`${translationPath}.errorMessage`),
                    palette: 'danger',
                    icon: (
                        <Icon icon="alert_hex" size="xLarge" color="red.400" />
                    ),
                    content: error.message
                });
            }
        },
        [
            closeToast,
            mutateAsync,
            showFailedToast,
            showSuccessToast,
            showToast,
            t,
            translationPath
        ]
    );

    const handleDownloadTemplate = () => {
        FileUtils.createAndDownloadCSV({
            fileName: 'waybill-import-template.csv',
            headers: ['waybillId'],
            rows: [{ waybillId: '{Mandatory}' }, { waybillId: 'waybill-id-01' }]
        });
    };

    const columns = React.useMemo(() => {
        return [
            {
                id: 'carrierId',
                accessor: 'carrierId',
                headerText: t(`${translationPath}.summaryHeader.carrierName`)
            },
            {
                id: 'serviceType',
                accessor: 'serviceType',
                headerText: t(`${translationPath}.summaryHeader.carrierType`)
            },
            {
                id: 'count',
                accessor: 'count',
                headerText: t(`${translationPath}.summaryHeader.count`)
            },
            {
                id: 'waybillGenerationType',
                accessor: 'waybillGenerationType',
                headerText: t(`${translationPath}.summaryHeader.type`)
            },
            {
                id: 'actions',
                headerText: t(`${translationPath}.summaryHeader.action`),
                Cell: ({ row }) => {
                    const {
                        original: { count, waybillGenerationType }
                    } = row;

                    if (
                        waybillGenerationType !== WaybillGenerationTypeEnum.LIST
                    ) {
                        return null;
                    }

                    return (
                        <Columns space="xSmall" alignY="center">
                            <UploadButton
                                onUpload={file => {
                                    handleUpload({
                                        file,
                                        row: row.original
                                    });
                                }}
                                text={t('translations:add')}
                            />

                            {count > 0 && (
                                <Button
                                    appearance="ghost"
                                    palette="primary"
                                    size="small"
                                    onClick={() => {
                                        setSelectedCarrier(row.original);
                                    }}
                                >
                                    {t('translations:view')}
                                </Button>
                            )}
                        </Columns>
                    );
                }
            }
        ];
    }, [handleUpload, t, translationPath]);

    const rows = waybillUnusedSummaryQuery.data?.rows;

    const items = t('translations:item', {
        count: rows?.size
    });

    return (
        <Flex flexDirection="column" height="100%">
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Columns space="small" alignY="center">
                    <Heading size="large">
                        {t(`${translationPath}.label`)}
                    </Heading>

                    {rows && rows.size > 0 && (
                        <Text size="intermediate" color="grey.600">
                            {`(${rows.size} ${items})`}
                        </Text>
                    )}

                    <Button
                        icon="download"
                        appearance="ghost"
                        palette="primary"
                        size="small"
                        onClick={handleDownloadTemplate}
                    >
                        {t(`${translationPath}.downloadTemplate`)}
                    </Button>
                </Columns>

                <Box width="340px">
                    <Input
                        type="text"
                        icon="search"
                        placeholder={t('translations:search')}
                        onChange={debounce(setSearchKey, 300)}
                    />
                </Box>
            </Flex>

            {waybillUnusedSummaryQuery.status === ApiStatusEnum.Loading ? (
                <Skeleton height={30} borderRadius={3} count={10} />
            ) : waybillUnusedSummaryQuery.status === ApiStatusEnum.Error ? (
                <Box height="300px">
                    <APIErrorScreen
                        actionLabel={t('retry')}
                        onActionClick={waybillUnusedSummaryQuery.refetch}
                        subtitle={t('genericDataFetchError')}
                    />
                </Box>
            ) : (
                <DataTable
                    columns={columns}
                    data={waybillUnusedSummaryQuery.data.rows}
                />
            )}

            {selectedCarrier && (
                <WaybillDialog
                    translationBasePath={translationPath}
                    carrierId={selectedCarrier.carrierId}
                    serviceType={selectedCarrier.serviceType}
                    onDismiss={() => setSelectedCarrier(null)}
                />
            )}
        </Flex>
    );
};

export default PregenSeries;
